import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Upload, Trash2, Calendar, BarChart3, AlertCircle, Edit3 } from 'lucide-react';
import axios from 'axios';
import { apiClient, API_ENDPOINTS } from '../config/api';

interface FileData {
  id: string;
  displayName?: string;
  fileName: string;
  originalFilename?: string;
  uploadedFileName?: string;
  anonymousDisplayName?: string | null;
  anonymousSequence?: number | null;
  isUserSelected?: boolean;
  uploadedAt: string;
  rowCount: number;
  columnCount: number;
  fileType?: 'construction' | 'business' | 'unknown' | string;
  yearRange?: { min?: number; max?: number } | null;
  yearRangeLabel?: string | null;
  ownerUsername?: string | null;
}

const extractErrorMessage = (err: unknown, fallback: string) => {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.error?.message || fallback;
  }
  return err instanceof Error ? err.message : fallback;
};

interface FileManagerProps {
  onFileSelect: (fileId: string | null) => void;
  selectedFileId: string | null;
  allowUpload?: boolean;
  canManageUserSelection?: boolean;
  canDeleteFiles?: boolean;
  showOwnerUsername?: boolean;
  title?: string;
  description?: string;
}

const FileManager: React.FC<FileManagerProps> = ({
  onFileSelect,
  selectedFileId,
  allowUpload = false,
  canManageUserSelection = false,
  canDeleteFiles = false,
  showOwnerUsername = true,
  title = '保存済みファイル',
  description,
}) => {
  const [files, setFiles] = useState<FileData[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);

  const visibleFileLabel = useCallback((file: FileData) => {
    if (!allowUpload && file.anonymousDisplayName) {
      return file.anonymousDisplayName;
    }
    return file.displayName || file.fileName;
  }, [allowUpload]);

  const fileTypeLabel = (type?: string) => {
    if (type === 'construction') return '工事';
    if (type === 'business') return '業務';
    return '不明';
  };

  const formatYearRange = (file: FileData) => {
    if (file.yearRangeLabel) return `${file.yearRangeLabel}年度`;
    const min = file.yearRange?.min;
    const max = file.yearRange?.max;
    if (min !== undefined && min !== null && max !== undefined && max !== null) {
      return `${min}-${max}年度`;
    }
    return '年度不明';
  };

  const groupedFiles = useMemo(() => {
    const sortFiles = (list: FileData[]) => {
      return [...list].sort((a, b) => {
        const aMax = a.yearRange?.max ?? -Infinity;
        const bMax = b.yearRange?.max ?? -Infinity;
        if (aMax !== bMax) return bMax - aMax;
        const aMin = a.yearRange?.min ?? aMax;
        const bMin = b.yearRange?.min ?? bMax;
        const aSpan = aMax - aMin;
        const bSpan = bMax - bMin;
        if (aSpan !== bSpan) return bSpan - aSpan;
        const aTime = new Date(a.uploadedAt).getTime();
        const bTime = new Date(b.uploadedAt).getTime();
        if (aTime !== bTime) return bTime - aTime;
        return visibleFileLabel(a).localeCompare(visibleFileLabel(b), 'ja');
      });
    };

    const construction = sortFiles(files.filter((file) => file.fileType === 'construction'));
    const business = sortFiles(files.filter((file) => file.fileType === 'business'));
    const unknown = sortFiles(files.filter((file) => file.fileType !== 'construction' && file.fileType !== 'business'));
    return { construction, business, unknown };
  }, [files, visibleFileLabel]);

  const fetchFiles = useCallback(async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.files);
      setFiles(response.data.files || []);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'ファイル一覧の取得に失敗しました。'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('displayName', file.name);

      const response = await apiClient.post(API_ENDPOINTS.upload, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      await fetchFiles();
      setSuccessMessage(`ファイル「${file.name}」をデータベースへ保存しました。`);
      setTimeout(() => setSuccessMessage(null), 5000);
      if (response.data.fileId) {
        onFileSelect(response.data.fileId);
      }
    } catch (err) {
      setError(extractErrorMessage(err, 'アップロードに失敗しました。'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    event.target.value = '';
  };

  const handleFileDelete = async (fileId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setDeletingFileId(fileId);
    setError(null);

    try {
      await apiClient.delete(API_ENDPOINTS.deleteFile(fileId));
      await fetchFiles();
      setDeleteConfirmId(null);
      if (selectedFileId === fileId) {
        onFileSelect(null);
      }
      setSuccessMessage('ファイルを削除しました。');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(extractErrorMessage(err, '削除に失敗しました。'));
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleFileRename = async (fileId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const targetFile = files.find((file) => file.id === fileId);
    const newName = window.prompt('新しいファイル名を入力してください', targetFile?.displayName || targetFile?.fileName || '');
    if (newName === null) {
      return;
    }

    const trimmed = newName.trim();
    if (!trimmed) {
      setError('ファイル名を空にはできません。');
      return;
    }

    try {
      await apiClient.patch(API_ENDPOINTS.updateFile(fileId), { displayName: trimmed });
      await fetchFiles();
      setSuccessMessage(`ファイル名を「${trimmed}」に変更しました。`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(extractErrorMessage(err, 'ファイル名の変更に失敗しました。'));
    }
  };

  const handleUserSelection = async (file: FileData, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await apiClient.patch(API_ENDPOINTS.updateFile(file.id), {
        isUserSelected: !file.isUserSelected,
      });
      await fetchFiles();
      setSuccessMessage(
        file.isUserSelected
          ? `${visibleFileLabel(file)}の一般ユーザー向け選択を解除しました。`
          : `${visibleFileLabel(file)}を一般ユーザー向けに選択しました。`
      );
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(extractErrorMessage(err, '一般ユーザー向けファイルの選択に失敗しました。'));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderFileCard = (file: FileData) => (
    <div
      key={file.id}
      className={`file-item ${selectedFileId === file.id ? 'selected' : ''} ${canManageUserSelection && file.isUserSelected ? 'user-selected' : ''}`}
      onClick={() => onFileSelect(file.id)}
    >
      <div className="file-info">
        <div className="file-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FileText size={20} />
          <span>{visibleFileLabel(file)}</span>
        </div>
        {allowUpload && file.anonymousDisplayName && (
          <div className="file-details" style={{ marginTop: 6 }}>
            <span className="file-stats">アップロードファイル名: {file.uploadedFileName || file.originalFilename || file.displayName || file.fileName}</span>
            <span className="file-stats">匿名ファイル名: {file.anonymousDisplayName}</span>
          </div>
        )}
        <div className="file-meta" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: 6 }}>
          <span className="file-type-badge">【{fileTypeLabel(file.fileType)}】</span>
          <span className="year-range-badge">{formatYearRange(file)}</span>
          {showOwnerUsername && file.ownerUsername && <span className="year-range-badge">登録者: {file.ownerUsername}</span>}
        </div>
        <div className="file-details">
          <span className="file-stats">
            <BarChart3 size={14} />
            {file.rowCount.toLocaleString()} 行 × {file.columnCount} 列
          </span>
          <span className="file-date">
            <Calendar size={14} />
            {formatDate(file.uploadedAt)}
          </span>
        </div>
      </div>

      <div className="file-actions">
        <button
          className="analyze-file-button"
          onClick={(event) => {
            event.stopPropagation();
            onFileSelect(file.id);
          }}
        >
          データ分析をする
        </button>
        {selectedFileId === file.id && (
          <div className="selected-indicator">
            <span>選択中</span>
          </div>
        )}
        {canManageUserSelection && (file.fileType === 'construction' || file.fileType === 'business') && (
          <button
            className={`user-selection-button ${file.isUserSelected ? 'active' : ''}`}
            onClick={(event) => handleUserSelection(file, event)}
          >
            {file.isUserSelected ? '一般ユーザー向け選択中' : '一般ユーザー向けに選択'}
          </button>
        )}
        {allowUpload && (
          <>
            <button
              className="rename-button"
              onClick={(event) => handleFileRename(file.id, event)}
              title="ファイル名を変更"
            >
              <Edit3 size={18} />
            </button>
          </>
        )}
        {(allowUpload || canDeleteFiles) && (
          deleteConfirmId === file.id ? (
            <div className="delete-confirmation" onClick={(event) => event.stopPropagation()}>
              <span>本当に削除しますか？</span>
              <button
                className="delete-confirm-button"
                onClick={(event) => handleFileDelete(file.id, event)}
                disabled={deletingFileId === file.id}
              >
                {deletingFileId === file.id ? '削除中...' : '削除'}
              </button>
              <button
                className="delete-cancel-button"
                onClick={(event) => {
                  event.stopPropagation();
                  setDeleteConfirmId(null);
                }}
                disabled={deletingFileId === file.id}
              >
                キャンセル
              </button>
            </div>
          ) : (
            <button
              className="delete-button"
              onClick={(event) => {
                event.stopPropagation();
                setDeleteConfirmId(file.id);
              }}
              title="ファイルを削除"
            >
              <Trash2 size={16} />
              削除
            </button>
          )
        )}
      </div>
    </div>
  );

  const renderGroup = (title: string, items: FileData[]) => (
    <div className="file-group">
      <div className="file-group-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>{title}</span>
        </h3>
        <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>{items.length}件</span>
      </div>
      {items.length === 0 ? (
        <div className="empty-state" style={{ padding: '1rem', border: '1px dashed #e5e7eb', borderRadius: 8 }}>
          <p style={{ marginBottom: 0, color: '#6b7280' }}>
            {canManageUserSelection || allowUpload ? '該当するファイルがありません' : '選択されていません'}
          </p>
        </div>
      ) : (
        items.map(renderFileCard)
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="file-manager-loading">
        <div className="loading-spinner"></div>
        <p>ファイル一覧を読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="file-manager">
      <div className="file-manager-header">
        <div className="file-manager-heading">
          <h2 className="file-manager-title">
            <FileText className="title-icon" />
            {title}
          </h2>
          {description && <p className="file-manager-description">{description}</p>}
        </div>
        {allowUpload && (
          <div className="file-upload-section">
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileInputChange}
              disabled={isUploading}
              className="file-input"
              id="file-upload"
            />
            <label htmlFor="file-upload" className="file-upload-button">
              <Upload size={16} />
              {isUploading ? 'アップロード中...' : '新しいファイル'}
            </label>
          </div>
        )}
        {!allowUpload && (
          <div className="file-manager-readonly-badge">
            閲覧専用
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          <span>{successMessage}</span>
        </div>
      )}

      <div style={{ display: 'grid', gap: '1.5rem' }}>
        {renderGroup('工事ファイル', groupedFiles.construction)}
        {renderGroup('業務ファイル', groupedFiles.business)}
        {groupedFiles.unknown.length > 0 && renderGroup('その他', groupedFiles.unknown)}
      </div>
    </div>
  );
};

export default FileManager;
